import type { FunctionComponent } from 'react'
import { useState } from 'react'
import {
  PublicLayout,
  PublicCard,
  notify,
  NotificationLevel,
} from '@retailer-platform/shared-components'
import { Button, Checkbox, Flex } from '@retailer-platform/shared-components/src/tds'
import { useAcceptPartnerAgreementMutation, AgreementTypeEnum } from '../../../../__codegen__/api'
import { useDashMessages } from '../../../../intl/intl.hooks'
import { usePartnerContext } from '../../../../exports/routing'
import { PartnerType } from '../../../../exports/utils'
import { IdpTerms, PartnerDashboardTerms } from './Terms'

export const HaltTerms: FunctionComponent<{ refetchCurrentAccount: () => void }> = ({
  refetchCurrentAccount,
}) => {
  const [acceptPartnerAgreement, { loading: isAccepting }] = useAcceptPartnerAgreementMutation({
    refetchQueries: ['FetchCurrentEnterpriseAccount'],
  })
  const partnerContext = usePartnerContext()
  const [agreedToTerms, setAgreedToTerms] = useState(false)

  const messages = useDashMessages({
    idpTitle: 'app.halt.terms.idpTitle',
    sitewideTitle: 'app.halt.terms.sitewideTitle',
    agree: 'app.halt.terms.agree',
    continue: 'app.halt.terms.continue',
    accepting: 'app.halt.terms.accepting',
    error: 'app.halt.terms.error',
  })

  const handleCheck = () => {
    setAgreedToTerms(!agreedToTerms)
  }

  const expectedPartnerAgreementType =
    partnerContext.partnerType === PartnerType.IDP
      ? AgreementTypeEnum.IdpV1
      : AgreementTypeEnum.PartnerDashboardV1

  const handleSubmit = async () => {
    if (agreedToTerms) {
      const { data, errors } = await acceptPartnerAgreement({
        variables: {
          // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
          partnerId: partnerContext.partnerId,
          agreementType: expectedPartnerAgreementType,
        },
      })

      if (data?.enterpriseAccountAcceptPartnerAgreement?.acceptedPartnerAgreement?.partnerId) {
        refetchCurrentAccount()
      }

      if (errors) {
        notify({
          level: NotificationLevel.Error,
          body: messages.error,
        })
      }
    }
  }

  return (
    <PublicLayout>
      <PublicCard css={{ '> div': { gap: '0px' } }}>
        <div>
          <PublicCard.Title>
            {partnerContext.partnerType === PartnerType.IDP
              ? messages.idpTitle
              : messages.sitewideTitle}
          </PublicCard.Title>
          {partnerContext.partnerType === PartnerType.IDP ? (
            <IdpTerms />
          ) : (
            <PartnerDashboardTerms />
          )}
        </div>
        <Flex.Row justifyContent="space-between" alignItems="center">
          <Checkbox
            label={messages.agree}
            id="app.halt.terms.agree"
            onChange={() => handleCheck()}
            checked={agreedToTerms}
            data-testid="halt-terms-checkbox"
          />
          <Button
            disabled={!agreedToTerms || isAccepting}
            onClick={() => handleSubmit()}
            data-testid="halt-terms-button"
          >
            {isAccepting ? messages.accepting : messages.continue}
          </Button>
        </Flex.Row>
      </PublicCard>
    </PublicLayout>
  )
}
